import type { FC } from 'react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { TextDesktop as Text } from '@alfa-bank/site-text-v2/desktop';
import { Aligner } from '@alfa-bank/site-aligner';
import { ButtonDesktop as Button } from '@alfa-bank/site-button-v2/desktop';
import { Link } from '@alfa-bank/site-link';
import { Grid } from '@alfa-bank/site-grid-universal';
import { FormDesktop as Form } from '@alfa-bank/site-form/desktop';
import { FormProgressBarV2Desktop } from '@alfa-bank/site-form-progress-bar-v2/desktop';

import {
    downloadDocumentSignInformation,
    routeSwitch,
    selectAppMeta,
    selectDocumentSignInformation,
    selectShowProgressBar,
} from '../../store';
import { API_INVEST_SMEV_APPLICATIONS, modalSteps, formSteps } from '../../config';
import { getBirthday, getFullNameByParts } from '../../helpers/transform';
import { sendEventSmev } from '../../helpers/proxy-send-event';

import type { TDocumentsSignInformationClientDesktop } from './models';
import styles from './styles/styles.css';
import {
    defaultDocumentSignInformationStepSettings,
    defaultDocumentSignInformationStepTitleDesktop,
} from './config/default-values';
import { analyticsDocumentsSignInformationClientSmev } from './analytics';

export const DocumentsSignInformationClientStepDesktop: FC<
    TDocumentsSignInformationClientDesktop
> = ({
    addressLabel = defaultDocumentSignInformationStepSettings.addressLabel,
    birthdateLabel = defaultDocumentSignInformationStepSettings.birthdateLabel,
    fioLabel = defaultDocumentSignInformationStepSettings.fioLabel,
    passportLabel = defaultDocumentSignInformationStepSettings.passportLabel,
    primaryButton = defaultDocumentSignInformationStepSettings.primaryButton,
    secondaryButton = defaultDocumentSignInformationStepSettings.secondaryButton,
    progressBarSettings = defaultDocumentSignInformationStepSettings.progressBarSettings,
    showDownloadLinkAs = defaultDocumentSignInformationStepSettings.showDownloadLinkAs,
    title = defaultDocumentSignInformationStepTitleDesktop,
}) => {
    const showProgressBar = useSelector(selectShowProgressBar);
    const { uuid } = useSelector(selectAppMeta);
    const { isDocumentFileLoading, isLoading } = useSelector(selectDocumentSignInformation);
    const { address, birthday, firstName, lastName, middleName, passportNumber, passportSeries } =
        useSelector(selectAppMeta);
    const dispatch = useDispatch();

    const handleDownloadDocumentClick = () => {
        sendEventSmev(analyticsDocumentsSignInformationClientSmev.documentDownloadClick);
        dispatch(downloadDocumentSignInformation(isDocumentFileLoading));
    };
    const handleMoveToChangingData = () => {
        sendEventSmev(analyticsDocumentsSignInformationClientSmev.documentChangingDataClick);
        dispatch(routeSwitch(modalSteps.changing_address));
    };
    const handleClickDocumentSign = () => {
        sendEventSmev(analyticsDocumentsSignInformationClientSmev.documentSignClick);
        dispatch(routeSwitch(formSteps.documents_sign));
    };

    return (
        <Form view="without-shadow">
            {title && (
                <Aligner styler={{ marginBottom: 24 }}>
                    <Text styler={{ marginTop: 0, marginBottom: 0 }} tag="h2" view="medium">
                        {title}
                    </Text>
                </Aligner>
            )}
            {showProgressBar && (
                <FormProgressBarV2Desktop
                    caption={progressBarSettings.caption}
                    currentStepName={progressBarSettings.currentStepName}
                    hatchValue={progressBarSettings.hatchValue}
                    header={progressBarSettings.header}
                    line={progressBarSettings.line}
                    maxStep={progressBarSettings.maxStep}
                    nextStepName={progressBarSettings.nextStepName}
                    steps={progressBarSettings.steps}
                    styler={{ marginBottom: 24 }}
                    value={progressBarSettings.value}
                />
            )}
            <Text styler={{ marginBottom: 24, marginTop: 0 }} view="primary-medium">
                Для подтверждения{' '}
                {showDownloadLinkAs === 'button' ? (
                    <>
                        <Link
                            className={cn(styles.external, styles.link)}
                            onClick={handleDownloadDocumentClick}
                        >
                            заявления
                        </Link>{' '}
                    </>
                ) : (
                    <>
                        <Link
                            className={cn(styles.external, styles.link)}
                            // TODO временное решение и в будущем проверки не должно будет быть
                            href={`${API_INVEST_SMEV_APPLICATIONS}/${uuid}/document`}
                            onClick={() => {
                                sendEventSmev(
                                    analyticsDocumentsSignInformationClientSmev.documentDownloadClick
                                );
                            }}
                            target="_blank"
                        >
                            заявления
                        </Link>{' '}
                    </>
                )}
                {'на открытие счёта \nпроверьте и подпишите его по СМС'}
            </Text>
            <Text kind="caption" styler={{ marginBottom: 0, marginTop: 0 }} view="primary-small">
                {fioLabel}
            </Text>
            <Text styler={{ marginBottom: 24, marginTop: 0 }} view="primary-medium">
                {getFullNameByParts({
                    firstName: firstName,
                    lastName: lastName,
                    middleName: middleName,
                })}
            </Text>
            <Text kind="caption" styler={{ marginBottom: 0, marginTop: 0 }} view="primary-small">
                {passportLabel}
            </Text>
            <Text styler={{ marginBottom: 24, marginTop: 0 }} view="primary-medium">
                {passportSeries}
                {' '}
                {passportNumber}
            </Text>
            <Text kind="caption" styler={{ marginBottom: 0, marginTop: 0 }} view="primary-small">
                {addressLabel}
            </Text>
            <Text styler={{ marginBottom: 24, marginTop: 0 }} view="primary-medium">
                {address?.fullName}
            </Text>
            <Text kind="caption" styler={{ marginBottom: 0, marginTop: 0 }} view="primary-small">
                {birthdateLabel}
            </Text>
            <Text styler={{ marginBottom: 24, marginTop: 0 }} view="primary-medium">
                {getBirthday(birthday)}
            </Text>
            <Grid collapse columnsWidth={[270, 24, 270]}>
                <Button
                    block
                    loading={isLoading}
                    onClick={handleMoveToChangingData}
                    size="s"
                    view="secondary"
                >
                    {secondaryButton}
                </Button>
                <></>
                <Button
                    block
                    loading={isLoading}
                    onClick={handleClickDocumentSign}
                    size="s"
                    view="primary"
                >
                    {primaryButton}
                </Button>
            </Grid>
        </Form>
    );
};
